import { Icon, Portal } from '@chakra-ui/react';
import {
  IQuoteRespSchema,
  IRateResSchema,
  PriceUpliftPercentageTypeEnum,
} from '@monetize/types/app';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { MdClose, MdInfo } from 'react-icons/md';
import { handleApiErrorToast } from '../../../api/axios';
import { useSaveQuoteUpliftPlan } from '../../../api/priceUpliftService';
import useOfferingRate from '../../../hooks/useOfferingRate';
import { useQuoteContext } from '../../../routes/Quotes/Quote/quoteContext';
import { useConfirmModal } from '../../../services/confirmModal';
import {
  buildUpliftSavePlan,
  deriveQuoteUpliftDrawerState,
  deriveQuoteUpliftSummary,
  getUpliftEligibleOfferings,
  isUpliftAmount,
  QuoteRenewalUpliftFormType,
} from '../../../utils/quoteRenewalUplift.utils';
import { useQuoteRenewalUplift } from './useQuoteRenewalUplift';
import { createUpliftCalculatorObj } from '../../../utils/upliftPrice.utils';
import {
  MBox,
  MButton,
  MCustomNumberInput,
  MDivider,
  MDrawer,
  MDrawerBody,
  MDrawerCloseButton,
  MDrawerContent,
  MDrawerFooter,
  MDrawerHeader,
  MDrawerOverlay,
  MDrawerPositioner,
  MFlex,
  MFormField,
  MGrid,
  MGridItem,
  MIcon,
  MInputGroup,
  MRadio,
  MRadioGroup,
  MSkeleton,
  MStack,
  MSwitch,
  MText,
  MTooltip,
  MVStack,
} from '../../Monetize';
import {
  getUpliftGridColumns,
  QuoteRenewalUpliftOfferingGroup,
} from './QuoteRenewalUpliftOfferingGroup';

type QuoteRenewalUpliftDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const QuoteRenewalUpliftDrawer = ({
  isOpen,
  onClose,
}: QuoteRenewalUpliftDrawerProps) => {
  const {
    isReadOnly,
    quoteData: { evaluateQuoteRules },
    quotePrices,
    productOfferingsData: {
      productOfferingsObj,
      loading: isLoadingProductOfferings,
    },
  } = useQuoteContext();
  const { quote, eligibleOfferings, summary } = useQuoteRenewalUplift();
  const { showConfirmModal, closeModal: closeConfirmationModal } =
    useConfirmModal();
  const { fetchOfferingRate } = useOfferingRate();
  const [offeringRatesById, setOfferingRatesById] = useState<
    Record<string, IRateResSchema>
  >({});
  // Stays true once the first fetch settles — a rate that fails to load (the
  // hook already toasts) must not leave the drawer stuck on a skeleton.
  const [hasLoadedRates, setHasLoadedRates] = useState(false);

  // Seeded into the form on mount, re-applied while it is pristine, and
  // otherwise refreshed through explicit resets after a save.
  const defaultValues = useMemo(
    () => deriveQuoteUpliftDrawerState(eligibleOfferings, summary),
    [eligibleOfferings, summary],
  );

  const { control, formState, getValues, setValue, reset } =
    useForm<QuoteRenewalUpliftFormType>({ mode: 'onChange', defaultValues });
  const { isDirty } = formState;
  const formValues = useWatch({ control });
  const isIndividualMode = formValues.mode === 'individual';
  const headerAmount = formValues.amount ?? null;

  /**
   * The product catalog loads after the quote, so the drawer can open before
   * any offering is known to be eligible — leaving the form seeded with no
   * offerings at all. Re-seed once the real state is derivable; an edit the
   * user has already made always wins.
   */
  useEffect(() => {
    if (!isDirty) {
      reset(defaultValues);
    }
  }, [defaultValues, isDirty, reset]);

  /**
   * What Save would actually apply. Drives both the Save button and the
   * unsaved-changes prompt rather than `isDirty`, which is wrong in both
   * directions: an offering with no configuration opens already included, so
   * there can be work to do before the user touches anything, and an edit can
   * be undone without the form ever going back to pristine. A read-only drawer
   * has nothing to apply, so it must not offer to discard "changes" the user
   * could never have made — and could not save if they had. `buildUpliftSavePlan`
   * already skips each locked offering on its own, so a lock elsewhere on the
   * quote must not block the rest from saving.
   */
  const savePlan = useMemo(
    () =>
      formValues.offerings && !isReadOnly
        ? buildUpliftSavePlan(
            formValues as QuoteRenewalUpliftFormType,
            eligibleOfferings,
          )
        : [],
    [formValues, eligibleOfferings, isReadOnly],
  );

  const { mutateAsync: saveQuoteUpliftPlan, isPending } =
    useSaveQuoteUpliftPlan();
  // Gates the whole body: interacting before the seed lands would dirty the
  // form and block the re-seed above.
  const isLoadingUpliftData = isLoadingProductOfferings || !hasLoadedRates;

  const rateIds = useMemo(
    () =>
      eligibleOfferings
        .map(({ displayQuoteOffering }) => displayQuoteOffering.rateId)
        .filter(Boolean),
    [eligibleOfferings],
  );

  /**
   * A locked configuration is already committed to a generated renewal quote,
   * so `buildUpliftSavePlan` skips it — the drawer must not rewrite its form
   * values either, or the row would show an uplift that can never be saved.
   */
  const lockedQuoteOfferingIds = useMemo(
    () =>
      new Set(
        eligibleOfferings
          .filter(({ config }) => config?.locked)
          .map(({ parentQuoteOffering }) => parentQuoteOffering.id),
      ),
    [eligibleOfferings],
  );

  useEffect(() => {
    // Eligibility comes from the product catalog, so fetching before it lands
    // would settle on an empty rate set and drop the skeleton too early.
    if (isLoadingProductOfferings) {
      return;
    }
    let isCancelled = false;
    Promise.all(rateIds.map((rateId) => fetchOfferingRate(rateId))).then(
      (rates) => {
        if (isCancelled) {
          return;
        }
        setOfferingRatesById(
          Object.fromEntries(
            rates
              .filter((rate): rate is IRateResSchema => !!rate)
              .map((rate) => [rate.id, rate]),
          ),
        );
        setHasLoadedRates(true);
      },
    );
    return () => {
      isCancelled = true;
    };
  }, [rateIds, fetchOfferingRate, isLoadingProductOfferings]);

  /** Display items per offering — last ramp segment, usage-enriched and sorted. */
  const itemsByQuoteOfferingId = useMemo(
    () =>
      Object.fromEntries(
        eligibleOfferings.map(
          ({ parentQuoteOffering, displayQuoteOffering }) => [
            parentQuoteOffering.id,
            createUpliftCalculatorObj(offeringRatesById, displayQuoteOffering)
              .parentQuoteOffering.items,
          ],
        ),
      ),
    [eligibleOfferings, offeringRatesById],
  );

  const resetFromQuote = (updatedQuote: IQuoteRespSchema) => {
    const updatedEligibleOfferings = getUpliftEligibleOfferings(
      updatedQuote,
      productOfferingsObj,
    );
    reset(
      deriveQuoteUpliftDrawerState(
        updatedEligibleOfferings,
        deriveQuoteUpliftSummary(updatedQuote, updatedEligibleOfferings),
      ),
    );
  };

  const handleClose = () => {
    // Mirrors the Save button: a form can be dirty — a switch toggled and
    // toggled back, say — while still having nothing left to apply.
    if (savePlan.length) {
      showConfirmModal({
        title: 'Unsaved Changes',
        description:
          'You have unsaved changes. Are you sure you want to close?',
        onYes: () => {
          onClose();
          closeConfirmationModal();
        },
        yesBtnProps: { variant: 'primary' },
        noBtnProps: { variant: 'cancel' },
      });
      return;
    }
    onClose();
    closeConfirmationModal();
  };

  /**
   * Entering individual mode re-seeds every product from the header percentage
   * so both views describe the same uplift: a uniform uplift reaches every
   * product, so it includes them all at that percentage rather than
   * resurrecting an older selection. A locked offering is left alone — its rows
   * are read-only and unsaveable, so they have to keep showing the
   * configuration already committed to the renewal quote.
   *
   * Nothing is re-seeded in the other two cases, so that an accidental click on
   * the switch is recoverable by clicking it again:
   *
   * - Leaving individual mode keeps the per-product values. Uniform mode
   *   neither renders nor saves them, so they are invisible until the switch
   *   comes back on — clearing them would only destroy work.
   * - Entering individual mode with no header percentage has nothing to
   *   distribute. This is how a `variable` quote opens (there is no single
   *   percentage to show in the header), so seeding here would overwrite the
   *   very per-product percentages the drawer was opened to edit with `null`,
   *   arming a save plan that deletes every configuration on the quote.
   */
  const handleModeChange = (isIndividual: boolean) => {
    setValue('mode', isIndividual ? 'individual' : 'uniform', {
      shouldDirty: true,
    });

    const amountToDistribute = getValues('amount');
    if (!isIndividual || !isUpliftAmount(amountToDistribute)) {
      return;
    }

    getValues('offerings').forEach(
      ({ quoteOfferingId, products }, offeringIndex) => {
        if (lockedQuoteOfferingIds.has(quoteOfferingId)) {
          return;
        }
        products.forEach((_, productIndex) => {
          setValue(
            `offerings.${offeringIndex}.products.${productIndex}.included`,
            true,
            { shouldDirty: true },
          );
          setValue(
            `offerings.${offeringIndex}.products.${productIndex}.amount`,
            amountToDistribute,
            { shouldDirty: true },
          );
        });
      },
    );
  };

  const handleSave = async () => {
    if (!quote) {
      return;
    }

    if (!savePlan.length) {
      onClose();
      return;
    }

    try {
      await saveQuoteUpliftPlan({ quoteId: quote.id, steps: savePlan });
      // Re-evaluate rules so approvals and totals reflect the new configuration.
      const updatedQuote = await evaluateQuoteRules(quote.id);
      if (updatedQuote) {
        resetFromQuote(updatedQuote);
      }
      onClose();
    } catch (error) {
      handleApiErrorToast(error);
      // Some steps may have applied — re-derive so a retry only sends what is
      // still outstanding.
      const updatedQuote = await evaluateQuoteRules(quote.id);
      if (updatedQuote) {
        resetFromQuote(updatedQuote);
      }
    }
  };

  if (!quote) {
    return null;
  }

  return (
    <MDrawer
      open={isOpen}
      size="lg"
      placement="end"
      onOpenChange={(e) => {
        if (!e.open) {
          handleClose();
        }
      }}
      preventScroll={false}
    >
      <Portal>
        <MDrawerOverlay />
        <MDrawerPositioner>
          <MDrawerContent>
            <MDrawerCloseButton data-testid="renewal-uplift-close-btn">
              <Icon asChild>
                <MdClose />
              </Icon>
            </MDrawerCloseButton>
            <MDrawerHeader
              fontSize="md"
              borderBottomColor="tGray.back"
              borderBottomWidth="1px"
              borderBottomStyle="solid"
            >
              Renewal Uplift
            </MDrawerHeader>

            <MDrawerBody bg="tGray.sidebarDark" color="tGray.darkPurple">
              <MText mb="4">
                Price increases can be automatically added to the renewal Quote.
                Set a percentage or a max limit for uplift pricing. Clearing the
                percentage removes renewal uplift from this quote.
              </MText>

              {isLoadingUpliftData ? (
                <MSkeleton h="24rem" w="full" />
              ) : (
                <>
                  <MBox bg="tWhite.base" borderRadius="md" p="4" mb="4">
                    <MFormField>
                      <Controller
                        control={control}
                        name="percentageType"
                        render={({ field: { value, onChange, ...rest } }) => (
                          <MRadioGroup
                            {...rest}
                            isDisabled={isReadOnly}
                            value={value}
                            onChange={onChange}
                          >
                            <MStack
                              gap={4}
                              direction="row"
                              color="tGray.darkPurple"
                            >
                              <MRadio
                                value={PriceUpliftPercentageTypeEnum.FIXED}
                                data-testid="renewal-uplift-fixed-radio"
                              >
                                Fixed Percentage
                              </MRadio>
                              <MRadio
                                value={PriceUpliftPercentageTypeEnum.MAX}
                                data-testid="renewal-uplift-max-radio"
                              >
                                <MText
                                  display="inline-flex"
                                  alignItems="center"
                                >
                                  Max Percentage
                                  <MTooltip
                                    label="Maximum percentage allowed for the price to increase"
                                    placement="bottom-start"
                                    shouldWrapChildren
                                  >
                                    <MIcon
                                      display="flex"
                                      alignItems="center"
                                      as={MdInfo}
                                      color="tPurple.base"
                                      mx={1}
                                    />
                                  </MTooltip>
                                </MText>
                              </MRadio>
                            </MStack>
                          </MRadioGroup>
                        )}
                      />
                    </MFormField>

                    <MDivider my="4" />

                    {!isIndividualMode && (
                      <MFormField label="Uplift Percentage" maxW="12rem" mb="4">
                        <Controller
                          control={control}
                          name="amount"
                          render={({ field: { value, onChange, ...rest } }) => (
                            <MInputGroup
                              endElement={<MText fontWeight="bold">%</MText>}
                            >
                              <MCustomNumberInput
                                {...rest}
                                inputMode="decimal"
                                isDisabled={isReadOnly}
                                value={value ?? ''}
                                onChange={(valueAsString, valueAsNumber) =>
                                  onChange(
                                    valueAsString === '' ? null : valueAsNumber,
                                  )
                                }
                                placeholder="0"
                                styleProps={{
                                  'data-testid': 'renewal-uplift-amount-input',
                                }}
                              />
                            </MInputGroup>
                          )}
                        />
                      </MFormField>
                    )}

                    <MFlex align="center" justify="space-between">
                      <MText>Apply individual uplifts per product</MText>
                      <MSwitch
                        isChecked={isIndividualMode}
                        isDisabled={isReadOnly}
                        onChange={({ target }) =>
                          handleModeChange(target.checked)
                        }
                        aria-label="Apply individual uplifts per product"
                        data-testid="renewal-uplift-individual-mode-switch"
                      />
                    </MFlex>
                  </MBox>

                  <MVStack gap="3" alignItems="stretch">
                    <MGrid
                      templateColumns={getUpliftGridColumns(isIndividualMode)}
                      columnGap="3"
                      px="4"
                    >
                      {isIndividualMode && <MGridItem />}
                      <MGridItem>
                        <MText fontWeight="semibold">Offering / Product</MText>
                      </MGridItem>
                      {isIndividualMode && (
                        <MGridItem>
                          <MText fontWeight="semibold">Uplift</MText>
                        </MGridItem>
                      )}
                      <MGridItem>
                        <MText fontWeight="semibold" textAlign="right">
                          Current PPU
                        </MText>
                      </MGridItem>
                      <MGridItem>
                        <MText fontWeight="semibold" textAlign="right">
                          Renewal PPU
                        </MText>
                      </MGridItem>
                    </MGrid>

                    {eligibleOfferings.map(
                      (eligibleOffering, offeringIndex) => {
                        // A lock only freezes the offering it belongs to — the save
                        // plan already skips it on its own, so the rest of the quote
                        // must stay editable through this drawer.
                        const isLocked = lockedQuoteOfferingIds.has(
                          eligibleOffering.parentQuoteOffering.id,
                        );
                        return (
                          <QuoteRenewalUpliftOfferingGroup
                            key={eligibleOffering.parentQuoteOffering.id}
                            control={control}
                            setValue={setValue}
                            offeringIndex={offeringIndex}
                            eligibleOffering={eligibleOffering}
                            items={
                              itemsByQuoteOfferingId[
                                eligibleOffering.parentQuoteOffering.id
                              ] ?? []
                            }
                            quote={quote}
                            quotePrices={quotePrices}
                            offeringRate={
                              offeringRatesById[
                                eligibleOffering.displayQuoteOffering.rateId
                              ]
                            }
                            isIndividualMode={isIndividualMode}
                            isDisabled={isReadOnly || isLocked}
                            isLocked={isLocked}
                            headerAmount={headerAmount}
                          />
                        );
                      },
                    )}
                  </MVStack>
                </>
              )}
            </MDrawerBody>

            <MDrawerFooter
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap="4"
            >
              <MButton type="button" variant="cancel" onClick={handleClose}>
                Close
              </MButton>
              {!isReadOnly && (
                <MButton
                  type="button"
                  variant="primary"
                  onClick={handleSave}
                  isDisabled={!savePlan.length || isPending}
                  isLoading={isPending}
                  data-testid="renewal-uplift-save-btn"
                >
                  Save
                </MButton>
              )}
            </MDrawerFooter>
          </MDrawerContent>
        </MDrawerPositioner>
      </Portal>
    </MDrawer>
  );
};
